import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaTimes, FaUpload } from 'react-icons/fa';
import ConfirmModal from '../common/ConfirmModal';
import './Attributes.css';
import { validateImageFile } from '../utils/fileValidation';

const namedColors = {
  red: '#ef4444',
  green: '#22c55e',
  blue: '#3b82f6',
  yellow: '#eab308',
  orange: '#f97316',
  purple: '#a855f7',
  pink: '#ec4899',
  black: '#000000',
  white: '#ffffff',
  gray: '#6b7280',
  grey: '#6b7280',
  brown: '#854d0e',
  cyan: '#06b6d4',
  magenta: '#d946ef',
  gold: '#c4a882',
  silver: '#94a3b8',
  navy: '#1e3a8a',
  teal: '#14b8a6',
  lime: '#84cc16',
  maroon: '#800000',
  olive: '#808000',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  nova: '#d4af37',
  graphite: '#374151',
  walnut: '#5c4033',
  champagne: '#f7e7ce',
  bronze: '#cd7f32'
};

const resolveColorToHex = (val) => {
  if (!val || typeof val !== 'string') return '#c4a882';
  const clean = val.trim().toLowerCase();
  if (namedColors[clean]) return namedColors[clean];
  if (/^#?[0-9a-f]{6}$/i.test(clean)) return clean.startsWith('#') ? clean : `#${clean}`;
  if (/^#?[0-9a-f]{3}$/i.test(clean)) {
    const raw = clean.startsWith('#') ? clean.slice(1) : clean;
    return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
  }
  return clean;
};

const Attributes = () => {
  // Lists loaded from backend
  const [attributes, setAttributes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('1'); // Default to Active (1) to show the 5 active entries
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterCategory, filterType, filterStatus]);

  // Modal controls
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attributeToEdit, setAttributeToEdit] = useState(null);

  // Form states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [attributeName, setAttributeName] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [assignedCategoryIds, setAssignedCategoryIds] = useState([]);
  const [options, setOptions] = useState([]); // Array of { id, type, label, value }
  const [isHighlighted, setIsHighlighted] = useState(false);

  // Dropdown toggle and search states
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  // Delete control state
  const [attributeToDelete, setAttributeToDelete] = useState(null);

  // Ref for click outside detection
  const categoryDropdownRef = useRef(null);

  const rawBackendHost = process.env.REACT_APP_BACKEND || 'http://localhost:8000';
  const backendHost = rawBackendHost.endsWith('/') ? rawBackendHost.slice(0, -1) : rawBackendHost;

  // Fetch initial data
  const fetchData = async () => {
    try {
      setLoading(true);
      const [catsRes, typesRes, attrsRes] = await Promise.all([
        axiosInstance.get('/categories'),
        axiosInstance.get('/attributes/types'),
        axiosInstance.get('/attributes')
      ]);

      setCategories(catsRes.data);
      setTypes(typesRes.data);
      setAttributes(attrsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load attributes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Click outside handler for category picker dropdown
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter attributes based on selections
  const filteredAttributes = attributes.filter((item) => {
    const matchesSearch = item.attribute.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      filterCategory === '' || (Array.isArray(item.assign_category) && item.assign_category.includes(String(filterCategory)));
    const matchesType = filterType === '' || String(item.type) === String(filterType);
    const matchesStatus = filterStatus === '' || String(item.status) === String(filterStatus);
    return matchesSearch && matchesCategory && matchesType && matchesStatus;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAttributes = filteredAttributes.slice(indexOfFirstItem, indexOfLastItem);

  // Toggle Attribute Status (Active = 1, Inactive = 3)
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 3 : 1;
      await axiosInstance.put(`/attributes/${id}`, { status: newStatus });
      setAttributes((prev) => prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item)));
      toast.success(`Attribute is now ${newStatus === 1 ? 'Active' : 'Inactive'}`);
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to toggle status.');
    }
  };

  // Category selection checkbox handler
  const handleCategoryCheckboxChange = (catId) => {
    const stringId = String(catId);
    if (assignedCategoryIds.includes(stringId)) {
      setAssignedCategoryIds((prev) => prev.filter((id) => id !== stringId));
    } else {
      setAssignedCategoryIds((prev) => [...prev, stringId]);
    }
  };

  // Option Row Add / Remove / Change
  const handleAddOptionRow = () => {
    const newId = options.length > 0 ? Math.max(...options.map((o) => o.id)) + 1 : 0;
    setOptions((prev) => [...prev, { id: newId, type: String(selectedTypeId), label: '', value: '' }]);
  };

  const handleRemoveOptionRow = (optionId) => {
    setOptions((prev) => prev.filter((opt) => opt.id !== optionId));
  };

  const handleOptionFieldChange = (optionId, field, val) => {
    setOptions((prev) => prev.map((opt) => (opt.id === optionId ? { ...opt, [field]: val } : opt)));
  };

  // Image Upload handler for file chooser inputs
  const handleImageFileChange = async (optionId, file, event) => {
    if (!file) return;
    if (!validateImageFile(file, event)) return;
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axiosInstance.post('/attributes/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      handleOptionFieldChange(optionId, 'value', response.data.filename);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('File upload failed.');
    }
  };

  const handleTypeChange = (e) => {
    const newTypeId = e.target.value;
    setSelectedTypeId(newTypeId);
    setOptions([{ id: 0, type: String(newTypeId), label: '', value: '' }]);
  };

  // Open Form Modal for Create
  const openCreateModal = () => {
    setIsEditMode(false);
    setEditingId(null);
    setAttributeName('');
    const defaultType = types.length > 0 ? String(types[0].id) : '';
    setSelectedTypeId(defaultType);
    setAssignedCategoryIds([]);
    setOptions([{ id: 0, type: defaultType, label: '', value: '' }]);
    setIsHighlighted(false);
    setShowCategoryDropdown(false);
    setCategorySearchQuery('');
    setShowFormModal(true);
  };

  // Open Form Modal for Edit
  const openEditModal = (attr) => {
    setIsEditMode(true);
    setEditingId(attr.id);
    setAttributeName(attr.attribute);
    setSelectedTypeId(String(attr.type));
    setAssignedCategoryIds(attr.assign_category ? attr.assign_category.map(String) : []);
    setIsHighlighted(attr.highlight === 1);

    const formattedOptions = (attr.value || []).map((valItem, idx) => ({
      id: valItem.id !== undefined ? valItem.id : idx,
      type: String(valItem.type || attr.type),
      label: valItem.label || '',
      value: valItem.value || ''
    }));
    setOptions(formattedOptions);
    setShowCategoryDropdown(false);
    setCategorySearchQuery('');
    setShowFormModal(true);
  };

  const handleConfirmEdit = () => {
    if (attributeToEdit) {
      const target = attributeToEdit;
      setAttributeToEdit(null);
      openEditModal(target);
    }
  };

  // Form Submit handler
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!attributeName.trim()) {
      toast.error('Attribute name is required.');
      return;
    }
    if (!selectedTypeId) {
      toast.error('Attribute type is required.');
      return;
    }
    if (assignedCategoryIds.length === 0) {
      toast.error('Please assign at least one category.');
      return;
    }

    for (let opt of options) {
      if (selectedTypeId === '1' && !opt.value) {
        toast.error('All Color options must have a Color code selected.');
        return;
      }
      if (selectedTypeId === '2' && !opt.value) {
        toast.error('All Image options must have a file uploaded.');
        return;
      }
      if (selectedTypeId === '4' && !opt.value) {
        toast.error('All Link options must have a valid URL.');
        return;
      }
      if (selectedTypeId === '3' && !opt.label && !opt.value) {
        toast.error('All Text options must have a label or value.');
        return;
      }
    }

    try {
      const payload = {
        attribute: attributeName.trim(),
        type: parseInt(selectedTypeId),
        assign_category: assignedCategoryIds,
        value: options.map((opt) => ({
          id: opt.id,
          type: String(selectedTypeId),
          label: opt.label.trim(),
          value: opt.value.trim()
        })),
        highlight: isHighlighted ? 1 : 0
      };

      if (isEditMode) {
        await axiosInstance.put(`/attributes/${editingId}`, payload);
        toast.success('Attribute updated successfully!');
      } else {
        await axiosInstance.post('/attributes', payload);
        toast.success('Attribute created successfully!');
      }

      setShowFormModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving attribute:', error);
      toast.error(error.response?.data?.message || 'Failed to save attribute.');
    }
  };

  // Delete handler
  const handleDeleteAttribute = async () => {
    try {
      await axiosInstance.delete(`/attributes/${attributeToDelete.id}`);
      toast.success('Attribute deleted successfully!');
      setShowDeleteModal(false);
      setAttributeToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting attribute:', error);
      toast.error(error.response?.data?.message || 'Failed to delete attribute.');
    }
  };

  return (
    <>
      <div className="premium-page-container animate-fade-up">
        {/* Header Block */}
        <div className="premium-page-header">
          <h2 className="page-title">Products Attribute Manager</h2>
          <button type="button" className="btn-add-attribute" onClick={openCreateModal}>
            <FaPlus size={13} />
            <span>Add Attribute</span>
          </button>
        </div>

        {/* Filter & Search Bar */}
        <div className="premium-controls-bar attr-controls-bar">
          <select
            className="premium-filter-select attr-filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.category || cat.title}
              </option>
            ))}
          </select>

          <select className="premium-filter-select attr-filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.type}
              </option>
            ))}
          </select>

          <select
            className="premium-filter-select attr-filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="1">Active Attributes</option>
            <option value="3">Inactive Attributes</option>
            <option value="">All Attributes</option>
          </select>

          <div className="attr-search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search here..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Attribute Table */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="premium-table-wrapper">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th style={{ width: '4%' }}>
                      <input type="checkbox" className="premium-checkbox" />
                    </th>
                    <th style={{ width: '12%' }}>Status</th>
                    <th style={{ width: '18%' }}>Attribute</th>
                    <th style={{ width: '10%' }}>Type</th>
                    <th style={{ width: '34%' }}>Value</th>
                    <th style={{ width: '12%' }}>Created on</th>
                    <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttributes.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '4rem', color: 'rgba(245, 240, 232, 0.4)' }}>
                        No attributes found.
                      </td>
                    </tr>
                  ) : (
                    currentAttributes.map((attr) => (
                      <tr key={attr.id}>
                        <td>
                          <input type="checkbox" className="premium-checkbox" />
                        </td>
                        <td>
                          <div className="switch-container">
                            <label className="switch" htmlFor={`status-toggle-${attr.id}`}>
                              <span
                                style={{
                                  position: 'absolute',
                                  width: '1px',
                                  height: '1px',
                                  padding: 0,
                                  margin: '-1px',
                                  overflow: 'hidden',
                                  clip: 'rect(0, 0, 0, 0)',
                                  border: 0
                                }}
                              >
                                Toggle Status
                              </span>
                              <input
                                id={`status-toggle-${attr.id}`}
                                type="checkbox"
                                checked={attr.status === 1}
                                onChange={() => handleToggleStatus(attr.id, attr.status)}
                              />
                              <span className="slider"></span>
                            </label>
                            <span className="switch-label">{attr.status === 1 ? 'Active' : 'Inactive'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="attribute-name-cell">
                            <span className="attribute-title-text">{attr.attribute}</span>
                            <span className="attribute-parent-text">
                              {attr.assignedCategories && attr.assignedCategories.length > 0
                                ? attr.assignedCategories.map((c) => c.title).join(', ')
                                : 'None'}
                            </span>
                          </div>
                        </td>
                        <td className="nowrap-cell">
                          <span className={`badge-type-pill ${attr.attributeType?.type?.toLowerCase().replace(/\s+/g, '') || 'text'}`}>
                            {attr.attributeType?.type || 'Text'}
                          </span>
                        </td>
                        <td>
                          <div className="options-preview-wrapper">
                            {attr.value &&
                              attr.value.slice(0, 3).map((valItem, idx) => {
                                const rawText = valItem.label || valItem.value || '';
                                const truncatedText = rawText.length > 18 ? rawText.substring(0, 18) + '...' : rawText;
                                const isColor = attr.type === 1 || attr.type === '1' || attr.attributeType?.type === 'Color';
                                const colorHex = isColor ? resolveColorToHex(valItem.value) : null;
                                return (
                                  <span
                                    key={idx}
                                    className="option-preview-tag"
                                    title={rawText}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                  >
                                    {isColor && colorHex && (
                                      <span
                                        style={{
                                          width: '10px',
                                          height: '10px',
                                          borderRadius: '50%',
                                          backgroundColor: colorHex,
                                          display: 'inline-block',
                                          border: '1px solid rgba(255,255,255,0.3)',
                                          flexShrink: 0
                                        }}
                                      />
                                    )}
                                    <span>
                                      {attr.attributeType?.type || 'Text'}: {truncatedText}
                                    </span>
                                  </span>
                                );
                              })}
                            {attr.value && attr.value.length > 3 && (
                              <span className="option-preview-tag more-tag">
                                +{attr.value.length - 3}
                                {/* Hover List Tooltip */}
                                <div className="options-hover-tooltip">
                                  {attr.value.slice(3).map((valItem, idx) => {
                                    const isColor = attr.type === 1 || attr.type === '1' || attr.attributeType?.type === 'Color';
                                    const colorHex = isColor ? resolveColorToHex(valItem.value) : null;
                                    return (
                                      <div
                                        key={idx}
                                        className="tooltip-item"
                                        title={valItem.label || valItem.value}
                                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                      >
                                        {isColor && colorHex && (
                                          <span
                                            style={{
                                              width: '10px',
                                              height: '10px',
                                              borderRadius: '50%',
                                              backgroundColor: colorHex,
                                              display: 'inline-block',
                                              border: '1px solid rgba(255,255,255,0.3)',
                                              flexShrink: 0
                                            }}
                                          />
                                        )}
                                        <span>
                                          {attr.attributeType?.type || 'Text'}: {valItem.label || valItem.value}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="nowrap-cell">{attr.createdAt}</td>
                        <td style={{ textAlign: 'center' }}>
                          <div className="inline-actions-container" style={{ justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="btn-inline-action edit-action"
                              title="Modify"
                              onClick={() => setAttributeToEdit(attr)}
                            >
                              <FaEdit size={14} />
                            </button>
                            <button
                              type="button"
                              className="btn-inline-action delete-action"
                              title="Delete"
                              onClick={() => {
                                setAttributeToDelete(attr);
                                setShowDeleteModal(true);
                              }}
                            >
                              <FaTrash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {filteredAttributes.length > 0 && (
              <div className="premium-pagination-container">
                <div className="premium-pagination-info">
                  Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredAttributes.length)}</span> of{' '}
                  <span>{filteredAttributes.length}</span> entries
                </div>
                <div className="premium-pagination-list">
                  <button className="premium-pagination-btn arrow-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                    &laquo;
                  </button>
                  <button
                    className="premium-pagination-btn arrow-btn"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    &lsaquo;
                  </button>

                  {Array.from({ length: Math.ceil(filteredAttributes.length / itemsPerPage) }, (_, idx) => idx + 1)
                    .filter((page) => {
                      const totalPages = Math.ceil(filteredAttributes.length / itemsPerPage);
                      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                    })
                    .map((page, index, array) => {
                      return (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                          )}
                          <button
                            className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}

                  <button
                    className="premium-pagination-btn arrow-btn"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.ceil(filteredAttributes.length / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(filteredAttributes.length / itemsPerPage)}
                  >
                    &rsaquo;
                  </button>
                  <button
                    className="premium-pagination-btn arrow-btn"
                    onClick={() => setCurrentPage(Math.ceil(filteredAttributes.length / itemsPerPage))}
                    disabled={currentPage === Math.ceil(filteredAttributes.length / itemsPerPage)}
                  >
                    &raquo;
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* DYNAMIC FORM MODAL (ADD / MODIFY) */}
      {showFormModal && (
        <div className="premium-modal-overlay">
          <form onSubmit={handleFormSubmit} className="premium-modal-wrapper">
            <div className="premium-modal-header">
              <h3>{isEditMode ? 'Modify Product Attribute' : 'Create Product Attribute'}</h3>
              <button type="button" className="btn-close-modal" onClick={() => setShowFormModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="premium-modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '1.25rem' }}>
                {/* Attribute Title */}
                <div className="premium-form-group" style={{ minWidth: 0 }}>
                  <label htmlFor="attrName">
                    Attribute Title <span>*</span>
                  </label>
                  <input
                    type="text"
                    id="attrName"
                    className="premium-input"
                    placeholder="Attribute Title"
                    required
                    value={attributeName}
                    onChange={(e) => setAttributeName(e.target.value)}
                  />
                </div>

                {/* Type Selection */}
                <div className="premium-form-group" style={{ minWidth: 0 }}>
                  <label htmlFor="attrType">
                    Attribute Type <span>*</span>
                  </label>
                  <select
                    id="attrType"
                    className="premium-select"
                    required
                    disabled={isEditMode}
                    value={selectedTypeId}
                    onChange={handleTypeChange}
                  >
                    <option value="">Select Type</option>
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '1.25rem', alignItems: 'center' }}>
                {/* Category Assignment Custom Multi-Select Dropdown */}
                <div className="premium-form-group" ref={categoryDropdownRef} style={{ minWidth: 0 }}>
                  <span
                    className="premium-label-title"
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'rgba(245, 240, 232, 0.75)',
                      marginBottom: '8px',
                      letterSpacing: '0.3px'
                    }}
                  >
                    Assign to <span>*</span>
                  </span>

                  <div className="premium-multiselect-dropdown">
                    <button type="button" className="premium-select-trigger" onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}>
                      <span
                        className="trigger-text"
                        title={
                          assignedCategoryIds.length > 0
                            ? categories
                                .filter((c) => assignedCategoryIds.includes(String(c.id)))
                                .map((c) => c.category || c.title)
                                .join(', ')
                            : 'Select Category'
                        }
                      >
                        {assignedCategoryIds.length === 0
                          ? 'Select Category'
                          : categories
                              .filter((c) => assignedCategoryIds.includes(String(c.id)))
                              .map((c) => c.category || c.title)
                              .join(', ')}
                      </span>
                      <span className="dropdown-arrow-icon">▼</span>
                    </button>

                    {showCategoryDropdown && (
                      <div className="premium-dropdown-menu">
                        <input
                          type="text"
                          className="premium-input dropdown-search-input"
                          placeholder="Search..."
                          style={{
                            marginBottom: '0.65rem',
                            height: '34px',
                            padding: '0.4rem 0.75rem',
                            fontSize: '13px',
                            background: 'rgba(0, 0, 0, 0.25)',
                            borderColor: 'rgba(196, 168, 130, 0.2)'
                          }}
                          value={categorySearchQuery}
                          onChange={(e) => setCategorySearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing
                        />
                        {categories
                          .filter(
                            (cat) =>
                              cat.type === 'main' &&
                              cat.status === true &&
                              (cat.category || cat.title || '').toLowerCase().includes(categorySearchQuery.toLowerCase())
                          )
                          .map((cat) => {
                            const optionId = `cat-assign-${cat.id}`;
                            return (
                              <label key={cat.id} className="premium-dropdown-item" htmlFor={optionId}>
                                <input
                                  type="checkbox"
                                  id={optionId}
                                  className="multiselect-checkbox"
                                  checked={assignedCategoryIds.includes(String(cat.id))}
                                  onChange={() => handleCategoryCheckboxChange(cat.id)}
                                />
                                <span>{cat.category || cat.title}</span>
                              </label>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Highlight Toggle Option */}
                <div className="premium-form-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span
                    className="premium-label-title"
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'rgba(245, 240, 232, 0.75)',
                      letterSpacing: '0.3px'
                    }}
                  >
                    Highlight this attribute with ✔ : ✖
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label className="switch" htmlFor="highlight-toggle-form">
                      <span
                        style={{
                          position: 'absolute',
                          width: '1px',
                          height: '1px',
                          padding: 0,
                          margin: '-1px',
                          overflow: 'hidden',
                          clip: 'rect(0, 0, 0, 0)',
                          border: 0
                        }}
                      >
                        Highlight toggle
                      </span>
                      <input
                        id="highlight-toggle-form"
                        type="checkbox"
                        checked={isHighlighted}
                        onChange={() => setIsHighlighted(!isHighlighted)}
                      />
                      <span className="slider"></span>
                    </label>
                    <span className="switch-label" style={{ minWidth: 'unset' }}>
                      {isHighlighted ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dynamic Options Builder */}
              {selectedTypeId && (
                <div className="premium-form-group">
                  <div className="options-builder-container">
                    <div className="option-row-headers">
                      <span>
                        Attribute Label <span>*</span>
                      </span>
                      <span>
                        Attribute Value <span>*</span>
                      </span>
                      <span></span>
                    </div>

                    <div className="option-rows-list">
                      {options.map((opt) => {
                        const labelId = `opt-label-${opt.id}`;
                        const valueId = `opt-value-${opt.id}`;

                        return (
                          <div key={opt.id} className="option-row-item">
                            {/* Option Label / Name */}
                            <div style={{ width: '100%' }}>
                              <input
                                type="text"
                                id={labelId}
                                className="premium-input"
                                placeholder="Label"
                                value={opt.label}
                                onChange={(e) => handleOptionFieldChange(opt.id, 'label', e.target.value)}
                              />
                            </div>

                            {/* Option Value (Adapts according to Type selection) */}
                            <div style={{ width: '100%' }}>
                              {String(selectedTypeId) === '1' ? (
                                /* Color Picker Option with Live Swatch */
                                <div className="color-input-wrapper">
                                  <div
                                    className="color-picker-box"
                                    title="Click to choose color"
                                    style={{
                                      backgroundColor: resolveColorToHex(opt.value) || '#c4a882'
                                    }}
                                  >
                                    <input
                                      type="color"
                                      id={`color-picker-${opt.id}`}
                                      className="color-picker-input"
                                      value={
                                        resolveColorToHex(opt.value).startsWith('#') && resolveColorToHex(opt.value).length === 7
                                          ? resolveColorToHex(opt.value)
                                          : '#c4a882'
                                      }
                                      onChange={(e) => handleOptionFieldChange(opt.id, 'value', e.target.value)}
                                    />
                                  </div>
                                  <input
                                    type="text"
                                    id={valueId}
                                    className="premium-input"
                                    placeholder="Color (e.g. red, #ff0000)"
                                    required
                                    value={opt.value}
                                    onChange={(e) => handleOptionFieldChange(opt.id, 'value', e.target.value)}
                                  />
                                </div>
                              ) : String(selectedTypeId) === '2' ? (
                                /* Image File Chooser with preview */
                                <div className="image-upload-wrapper">
                                  <label className="btn-file-upload" htmlFor={`file-input-${opt.id}`}>
                                    <FaUpload size={12} />
                                    <span>Choose file</span>
                                  </label>
                                  <input
                                    id={`file-input-${opt.id}`}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => handleImageFileChange(opt.id, e.target.files[0], e)}
                                  />
                                  <span className="file-name-text" title={opt.value}>
                                    {opt.value || 'No file chosen'}
                                  </span>
                                  {opt.value && (
                                    <img
                                      src={`${backendHost}/uploads/${opt.value}`}
                                      alt="preview"
                                      className="image-row-thumbnail"
                                      onError={(e) => {
                                        const currentSrc = e.target.src || '';
                                        if (currentSrc.includes('aristo-india.com/backend/uploads/')) {
                                          e.target.src = `https://www.aristo-india.com/uploads/images/images/product-attribute/${opt.value}`;
                                        } else if (currentSrc.includes('product-attribute/')) {
                                          e.target.src = `https://www.aristo-india.com/uploads/${opt.value}`;
                                        } else if (!currentSrc.includes('aristo-india.com')) {
                                          e.target.src = `https://www.aristo-india.com/backend/uploads/${opt.value}`;
                                        } else {
                                          e.target.style.display = 'none';
                                        }
                                      }}
                                    />
                                  )}
                                </div>
                              ) : String(selectedTypeId) === '4' ? (
                                /* Text with Link (URL input) */
                                <input
                                  type="url"
                                  id={valueId}
                                  className="premium-input"
                                  placeholder="Link URL"
                                  required
                                  value={opt.value}
                                  onChange={(e) => handleOptionFieldChange(opt.id, 'value', e.target.value)}
                                />
                              ) : (
                                /* Simple Text Option Value */
                                <input
                                  type="text"
                                  id={valueId}
                                  className="premium-input"
                                  placeholder="Value"
                                  value={opt.value}
                                  onChange={(e) => handleOptionFieldChange(opt.id, 'value', e.target.value)}
                                />
                              )}
                            </div>

                            {/* Delete Option Row Button */}
                            <div>
                              <button
                                type="button"
                                className="btn-delete-option-row"
                                disabled={options.length <= 1}
                                onClick={() => handleRemoveOptionRow(opt.id)}
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add More button aligned right below options list */}
                    <div className="add-more-container">
                      <button type="button" className="btn-add-more" onClick={handleAddOptionRow}>
                        + Add More
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="premium-modal-footer">
              <button type="button" className="btn-premium-secondary" onClick={() => setShowFormModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn-premium-primary">
                Submit
              </button>
            </div>
          </form>
        </div>
      )}

      {/* EDIT CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={!!attributeToEdit}
        type="edit"
        title="Modify Attribute"
        itemName={attributeToEdit?.attribute}
        itemType="attribute"
        onConfirm={handleConfirmEdit}
        onCancel={() => setAttributeToEdit(null)}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={showDeleteModal && !!attributeToDelete}
        type="delete"
        title="Delete Attribute"
        itemName={attributeToDelete?.attribute}
        itemType="attribute"
        onConfirm={handleDeleteAttribute}
        onCancel={() => {
          setShowDeleteModal(false);
          setAttributeToDelete(null);
        }}
      />
    </>
  );
};

export default Attributes;
